import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import type { AccessLevel, MemberPermissions as Perms, PermissionCatalog, PermissionRole } from '../lib/types';
import { useParent } from './ParentContext';

/** A role's name, from the server's catalog (#471). */
export const roleLabel = (catalog: PermissionCatalog | undefined, r: PermissionRole | undefined) => catalog?.roles.find((x) => x.value === r)?.label ?? 'Co-parent';

/**
 * Settings → Family and Sharing → a co-parent's permissions (#471), set by the family's owner: a role, or Custom with a level for each
 * area. The server enforces it everywhere; what they can't see disappears from their pages.
 */
export function MemberPermissionsEditor({
  familyId,
  member,
  catalog,
  onSaved,
}: {
  familyId: string;
  member: { id: string; name: string; permissions?: Perms | null };
  catalog: PermissionCatalog;
  onSaved: () => void;
}) {
  const { toast } = useParent();
  const current = member.permissions ?? { role: 'coparent' as const, levels: {} as Record<string, AccessLevel> };
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save(role: PermissionRole, levels?: Record<string, AccessLevel>) {
    setBusy(true);
    try {
      await api.put(`/api/family/${familyId}/members/${member.id}/permissions`, { role, ...(role === 'custom' ? { levels } : {}) });
      toast({ text: `${member.name} is now: ${roleLabel(catalog, role)}.`, tone: 'good' });
      onSaved();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="member-permissions stack">
      <div className="row-gap wrap">
        <span className="muted small with-icon">
          <ShieldCheck size={14} /> Can do:
        </span>
        <select
          value={current.role}
          disabled={busy}
          onChange={(e) => {
            const role = e.target.value as PermissionRole;
            if (role === 'custom') {
              setOpen(true);
              void save('custom', current.levels);
            } else void save(role);
          }}
          aria-label={`What ${member.name} can do`}
        >
          {catalog.roles.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label} · {r.hint}
            </option>
          ))}
        </select>
        {current.role === 'custom' && (
          <button className="btn small" onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide areas' : 'Choose areas'}
          </button>
        )}
      </div>
      {current.role === 'custom' && open && (
        <table className="permissions-grid">
          <tbody>
            {catalog.areas.map((a) => (
              <tr key={a.key}>
                <td>
                  {a.label}
                  <span className="muted small block">{a.hint}</span>
                </td>
                <td>
                  <select
                    value={current.levels[a.key] ?? 'none'}
                    disabled={busy}
                    onChange={(e) => void save('custom', { ...current.levels, [a.key]: e.target.value as AccessLevel })}
                    aria-label={`${member.name}: ${a.label}`}
                  >
                    {catalog.levels.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
